import { API_ROOT } from '../constants'
import api from '../utils/api'
import { isDevOrPreviewSite } from '../utils/general'
import { CELLO_SCRIPT, CELLO_SCRIPT_DEV, CELLO_PRODUCT_ID, CELLO_PRODUCT_ID_DEV } from '../constants'

const getCelloConfig = () => {
  if (isDevOrPreviewSite()) {
    return {
      productId: CELLO_PRODUCT_ID,
      scriptUrl: CELLO_SCRIPT
    }
  } else {
    return {
      productId: CELLO_PRODUCT_ID,
      scriptUrl: CELLO_SCRIPT
    }
  }
}

class CelloService {
  constructor() {
    this.isInitialized = false
    this.cello = null
    this.token = null
    this.ucc = null
  }

  async initCello(userProfile) {
    if (!userProfile?.email || !userProfile?.id) return
    if (this.isInitialized || this._bootRequested) return
    try {
      let token = localStorage.getItem('celloToken')
      if (!token) {
        const response = await api.post(`${API_ROOT}/v3/cello/token/generate`, {
          userId: userProfile.id
        })

        if (response.data?.token) {
          token = response.data.token
          localStorage.setItem('celloToken', token)
        } else {
          throw new Error('No token received from API')
        }
      }

      this.token = token

      window.cello = window.cello || { cmd: [] }
      window.cello.cmd.push(
        async function (cello) {
          try {
            await cello.boot({
              productId: getCelloConfig().productId,
              token: this.token,
              language: 'en',
              productUserDetails: {
                email: userProfile.email
              },
              launcher: {
                selector: '.celloClass'
              }
            })

            this.cello = cello
            this.isInitialized = true
            this._bootRequested = false
          } catch (error) {
            console.error('Failed to initialize Cello:', error)
            this._bootRequested = false
          }
        }.bind(this)
      )
    } catch (error) {
      console.error('Failed to fetch Cello token:', error)
    }
  }

  isReady() {
    return this.isInitialized && this.cello !== null
  }

  async initAndOpen(userProfile) {
    if (!userProfile?.email || !userProfile?.id) return

    if (this.isReady()) {
      this.cello?.open?.()
      return
    }

    await this.initCello(userProfile)
    const maxAttempts = 100
    let attempts = 0
    if (this._openTimer) clearTimeout(this._openTimer)
    const checkReady = () => {
      if (this.isReady()) {
        this.cello?.open?.()
        this._openTimer = null
      } else if (attempts < maxAttempts) {
        this._openTimer = setTimeout(checkReady, 100)
      } else {
        this._openTimer = null
      }
    }
    this._openTimer = setTimeout(checkReady, 0)
  }

  clearToken() {
    localStorage.removeItem('celloToken')
    this.token = null
    this.isInitialized = false
    this.cello = null
  }

  initUCC(ucc) {
    if (ucc) {
      const encodedUcc = encodeURIComponent(ucc)
      const expiry = new Date()
      expiry.setDate(expiry.getDate() + 30)
      document.cookie = `tiiny_ucc=${encodedUcc}; expires=${expiry.toUTCString()}; path=/`
      this.ucc = ucc
    } else {
      const cookieUCC = this.getCookieValue('tiiny_ucc')
      if (cookieUCC) {
        this.ucc = cookieUCC
      }
    }
  }

  getCookieValue(cookieName) {
    const cookies = document.cookie.split(';')
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=')
      if (name === cookieName) {
        return value ? decodeURIComponent(value) : null
      }
    }
    return null
  }

  getCurrentUCC() {
    if (this.ucc) return this.ucc
    const cookieUCC = this.getCookieValue('tiiny_ucc')
    if (cookieUCC) {
      this.ucc = cookieUCC
      return cookieUCC
    }
    return null
  }

  clearUCC() {
    this.ucc = null
    document.cookie = 'tiiny_ucc=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/'
  }

  async trackSignupEvent(userData, ucc) {
    try {
      const currentUCC = ucc || this.getCurrentUCC()
      if (!currentUCC) {
        return
      }
      const eventData = {
        ucc: currentUCC,
        trigger: 'new-signup',
        userData: {
          email: userData.email,
          name: userData.name || `${userData.firstName || ''} ${userData.lastName || ''}`.trim()
        }
      }

      await api.post(`${API_ROOT}/v3/cello/event`, eventData)
      this.clearUCC()
    } catch (error) {
      throw error
    }
  }
}

const celloService = new CelloService()

export default celloService
